import {
  BOOK_SIZE_LABELS,
  type BookSize,
  type BookStatus,
  type CategoryRef,
  type MetadataSourceId,
} from '../../shared/api';

export const STATUS_LABELS: Record<BookStatus, string> = {
  pending: 'Fetching details…',
  complete: 'Catalogued',
  not_found: 'No catalogue record',
  error: 'Lookup failed',
};

export const SOURCE_LABELS: Record<MetadataSourceId, string> = {
  open_library: 'Open Library',
  google_books: 'Google Books',
};

/** "A", "A or B", "A, B or C". */
export function formatAlternatives(items: readonly string[]): string {
  return items.length <= 1 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} or ${items.at(-1)}`;
}

interface Choices {
  category: CategoryRef | null;
  size: BookSize | null;
}

/** The category and size chosen on the scan page, e.g. "Owned · <200mm" ('' for neither). */
export function describeChoices({ category, size }: Choices): string {
  return [category?.name, size && BOOK_SIZE_LABELS[size]].filter(Boolean).join(' · ');
}

/**
 * What saving a book that's already in the collection changes, e.g. "Add to Owned · Set size to <200mm",
 * or once `done`, "Added to Owned · Size set to <200mm" ('' when nothing changes).
 */
export function describeUpdate(
  { category, size, categoryAdded, sizeChanged }: Choices & { categoryAdded?: boolean; sizeChanged?: boolean },
  done: boolean,
): string {
  return [
    categoryAdded && category && `${done ? 'Added to' : 'Add to'} ${category.name}`,
    sizeChanged && size && `${done ? 'Size set to' : 'Set size to'} ${BOOK_SIZE_LABELS[size]}`,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function formatAuthors(authors: readonly string[]): string {
  return authors.length > 0 ? authors.join(', ') : 'Unknown author';
}

export function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
